import { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import FitnessCenterIcon from "@mui/icons-material/FitnessCenter";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { BASE_PATH_TO_HOME } from "../../../base_routes_paths/data_paths";
import { BASE_PATH_TO_LEADERBOARD } from "../../../base_routes_paths/corporate_challenges_paths";
import AUTH_CONTEXT from "../../../store";
import ARENO_API_ENDPOINT from "../../../api";

// ── Config ────────────────────────────────────────────────────────────────────
const SLIDE_INTERVAL_MS  = 12000;
const PREFETCH_BEFORE_MS = 2000;

// ── Theme ─────────────────────────────────────────────────────────────────────
const BG      = "#0d0d14";
const CARD_BG = "#13131f";
const CARD_1  = "#1a0d2e";
const BORDER  = "#2a2a3d";
const PURPLE  = "#7c3aed";
const GLOW    = "rgba(124,58,237,0.35)";
const PRI     = "#ffffff";
const SEC     = "#8b8ba0";
const MEDAL   = { 1: "#D4A017", 2: "#6B7280", 3: "#A0522D" };

// ── Podium sizing ─────────────────────────────────────────────────────────────
const PODIUM_SIZE = {
  1: { avatar: 160, card: 300, mt: 0,  minH: 320, namePx: 36, scorePx: 52 },
  2: { avatar: 120, card: 240, mt: 40, minH: 240, namePx: 28, scorePx: 38 },
  3: { avatar: 120, card: 240, mt: 40, minH: 240, namePx: 28, scorePx: 38 },
  4: { avatar: 100, card: 200, mt: 80, minH: 160, namePx: 24, scorePx: 30 },
  5: { avatar: 100, card: 200, mt: 80, minH: 160, namePx: 24, scorePx: 30 },
};

// ── Podium Card ───────────────────────────────────────────────────────────────

const PodiumCard = ({ user, rank }) => {
  const isFirst     = rank === 1;
  const medal       = MEDAL[rank] ?? SEC;
  const { avatar: avatarSize, card: cardW, mt, minH, namePx, scorePx } =
    PODIUM_SIZE[rank] ?? PODIUM_SIZE[5];
  const hasTrophy   = rank <= 3;
  const borderColor = hasTrophy ? medal : PURPLE;

  return (
    <Box sx={{ flex: `0 0 ${cardW}px`, display: "flex", flexDirection: "column", alignItems: "center", mt: `${mt}px` }}>
      <Box sx={{ position: "relative", mb: `${-avatarSize * 0.28}px`, zIndex: 1 }}>
        {hasTrophy && (
          <EmojiEventsIcon sx={{ position: "absolute", top: -(avatarSize * 0.28), right: -(avatarSize * 0.1), fontSize: avatarSize * 0.34, color: medal, filter: `drop-shadow(0 0 8px ${medal}aa)`, zIndex: 2 }} />
        )}
        <Box sx={{ width: avatarSize, height: avatarSize, borderRadius: "50%", border: `${isFirst ? 4 : 3}px solid ${borderColor}`, boxShadow: `0 0 ${isFirst ? 28 : 18}px ${borderColor}66`, position: "relative" }}>
          <Box sx={{ width: "100%", height: "100%", borderRadius: "50%", overflow: "hidden", backgroundColor: "#1e1e30" }}>
            {user.ProfileImage ? (
              <img src={user.ProfileImage} alt={user.Name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            ) : (
              <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography sx={{ fontSize: avatarSize * 0.35, fontWeight: 900, color: SEC }}>
                  {user.Name?.[0]?.toUpperCase() ?? "?"}
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ position: "absolute", bottom: 0, right: 0, width: avatarSize * 0.32, height: avatarSize * 0.32, borderRadius: "50%", backgroundColor: borderColor, display: "flex", alignItems: "center", justifyContent: "center", border: `2px solid ${BG}`, zIndex: 3 }}>
            <Typography sx={{ fontSize: avatarSize * 0.14, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{rank}</Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{ width: "100%", borderRadius: 1.5, border: `1px solid ${isFirst ? PURPLE : BORDER}`, backgroundColor: isFirst ? CARD_1 : CARD_BG, boxShadow: `0 0 28px ${GLOW}, inset 0 0 40px ${GLOW}`, pt: `${avatarSize * 0.28 + 16}px`, pb: isFirst ? 4 : 2.5, px: 2, textAlign: "center", minHeight: `${minH}px`, display: "flex", flexDirection: "column", justifyContent: "center" }}>
        <Typography fontWeight={800} color={PRI} mb={0.5} sx={{ fontSize: namePx, lineHeight: 1.2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {user.Name}
        </Typography>
        {isFirst && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={0.75} mb={1.5}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: PURPLE }} />
            <Typography variant="body2" fontWeight={700} color={PURPLE} letterSpacing={1.5}>CURRENT CHAMPION</Typography>
          </Stack>
        )}
        <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={0.75}>
          <Typography fontWeight={900} color={PRI} lineHeight={1} sx={{ fontSize: scorePx }}>
            {user.ChallengeAtmpt?.replace(/[^0-9]/g, "") || "—"}
          </Typography>
          <Typography fontWeight={700} color={SEC} letterSpacing={1} sx={{ fontSize: scorePx * 0.45 }}>
            {user.ChallengeAtmpt?.replace(/[0-9\s]/g, "").toUpperCase() || ""}
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

// ── List Row ──────────────────────────────────────────────────────────────────

const ListRow = ({ user, rank }) => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 3, py: 2, borderRadius: 1, backgroundColor: "#1a1a2a", border: `1px solid ${BORDER}` }}>
    <Typography variant="h6" fontWeight={700} sx={{ width: 48, flexShrink: 0, color: SEC, fontVariantNumeric: "tabular-nums" }}>
      {String(rank).padStart(2, "0")}
    </Typography>
    <Box sx={{ width: 52, height: 52, borderRadius: "50%", border: `1px solid ${BORDER}`, flexShrink: 0, overflow: "hidden", backgroundColor: "#1e1e30" }}>
      {user.ProfileImage ? (
        <img src={user.ProfileImage} alt={user.Name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Typography sx={{ fontSize: 20, fontWeight: 900, color: SEC }}>{user.Name?.[0]?.toUpperCase() ?? "?"}</Typography>
        </Box>
      )}
    </Box>
    <Typography variant="h5" fontWeight={700} color={PRI} noWrap sx={{ flex: 1, minWidth: 0 }}>{user.Name}</Typography>
    <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ flexShrink: 0, minWidth: 90, justifyContent: "flex-end" }}>
      <Typography variant="h5" fontWeight={900} color={PRI} lineHeight={1}>
        {user.ChallengeAtmpt?.replace(/[^0-9]/g, "") || "—"}
      </Typography>
      <Typography variant="body2" fontWeight={700} sx={{ color: SEC }}>
        {user.ChallengeAtmpt?.replace(/[0-9\s]/g, "").toUpperCase() || ""}
      </Typography>
    </Stack>
  </Stack>
);

// ── Skeletons ─────────────────────────────────────────────────────────────────

const PodiumSkeleton = ({ large }) => (
  <Box sx={{ flex: large ? "0 0 220px" : "0 0 185px", mt: large ? 0 : "32px", display: "flex", flexDirection: "column", alignItems: "center" }}>
    <Box sx={{ mb: "-28px", zIndex: 1 }}>
      <Skeleton variant="circular" width={large ? 100 : 80} height={large ? 100 : 80} sx={{ bgcolor: "#1e1e30" }} />
    </Box>
    <Skeleton variant="rounded" width="100%" height={large ? 160 : 130} sx={{ borderRadius: 2, bgcolor: "#1e1e30" }} />
  </Box>
);

const RowSkeleton = () => (
  <Stack direction="row" alignItems="center" spacing={2} sx={{ px: 2.5, py: 1.75, borderRadius: 1, backgroundColor: "#1a1a2a", border: `1px solid ${BORDER}` }}>
    <Skeleton variant="text" width={28} sx={{ bgcolor: "#1e1e30" }} />
    <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: "#1e1e30" }} />
    <Box sx={{ flex: 1 }}><Skeleton variant="text" width="40%" sx={{ bgcolor: "#1e1e30" }} /></Box>
    <Skeleton variant="text" width={60} sx={{ bgcolor: "#1e1e30" }} />
  </Stack>
);

// ── Col Header ────────────────────────────────────────────────────────────────

const ColHeader = ({ label = "COUNT" }) => (
  <Stack direction="row" alignItems="center" spacing={1.5} sx={{ px: 3, pb: 1.5, mb: 0.5 }}>
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, width: 48 }}>RANK</Typography>
    <Box sx={{ width: 52, flexShrink: 0 }} />
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, flex: 1 }}>PARTICIPANT</Typography>
    <Typography variant="body1" fontWeight={700} sx={{ color: SEC, minWidth: 90, textAlign: "right" }}>{label}</Typography>
  </Stack>
);

// ── Leaderboard Slide ─────────────────────────────────────────────────────────

const LeaderboardSlide = ({ slide, users, isLoading }) => {
  const isCombined = slide.type === "combined";
  const top5 = users.slice(0, 5);
  const rest = users.slice(5);

  const podiumOrder =
    top5.length === 5
      ? [
          { user: top5[3], rank: 4 },
          { user: top5[1], rank: 2 },
          { user: top5[0], rank: 1 },
          { user: top5[2], rank: 3 },
          { user: top5[4], rank: 5 },
        ]
      : top5.map((u, i) => ({ user: u, rank: i + 1 }));

  const half     = Math.ceil(rest.length / 2);
  const colLeft  = rest.slice(0, half);
  const colRight = rest.slice(half);

  return (
    <Box>
      {/* Slide title */}
      <Stack direction="row" alignItems="center" spacing={1.5} mb={4}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2, background: isCombined ? `linear-gradient(135deg, ${PURPLE} 0%, #1a0d2e 100%)` : PURPLE, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 0 16px ${GLOW}` }}>
          {isCombined
            ? <LeaderboardIcon sx={{ color: "#FFD700", fontSize: 20 }} />
            : <FitnessCenterIcon sx={{ color: "#fff", fontSize: 20 }} />
          }
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="h6" fontWeight={700} color={PRI} lineHeight={1.2}>
              {slide.Name}
            </Typography>
            {isCombined && (
              <Box sx={{ px: 1, py: 0.25, borderRadius: 0.75, backgroundColor: `${PURPLE}33`, border: `1px solid ${PURPLE}66` }}>
                <Typography variant="caption" fontWeight={700} color={PURPLE} letterSpacing={1}>COMBINED</Typography>
              </Box>
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: SEC }}>
            {slide.CorporateID} · {isCombined ? "Overall standings across all challenges" : "Ranked by performance"}
          </Typography>
        </Box>
      </Stack>

      {/* Podium */}
      {isLoading ? (
        <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={2} mb={5}>
          {[false, false, true, false, false].map((large, i) => (
            <PodiumSkeleton key={i} large={large} />
          ))}
        </Stack>
      ) : top5.length > 0 ? (
        <Stack direction="row" justifyContent="center" alignItems="flex-end" spacing={2} mb={5}>
          {podiumOrder.map(({ user, rank }) => (
            <PodiumCard key={`${user._id}_${rank}`} user={user} rank={rank} />
          ))}
        </Stack>
      ) : (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography color={SEC}>No participants yet</Typography>
        </Box>
      )}

      {/* Rest of list — two columns */}
      {(isLoading || rest.length > 0) && (
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3 }}>
          <Box>
            <ColHeader label={isCombined ? "POINTS" : "COUNT"} />
            <Stack spacing={1.5}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)
                : colLeft.map((user, i) => <ListRow key={user._id} user={user} rank={i + 6} />)}
            </Stack>
          </Box>
          <Box>
            <ColHeader label={isCombined ? "POINTS" : "COUNT"} />
            <Stack spacing={1.5}>
              {isLoading
                ? Array.from({ length: 3 }).map((_, i) => <RowSkeleton key={i} />)
                : colRight.map((user, i) => <ListRow key={user._id} user={user} rank={i + 6 + half} />)}
            </Stack>
          </Box>
        </Box>
      )}
    </Box>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const AllLeaderboards = () => {
  const { token } = AUTH_CONTEXT();

  // slides: { _id, Name, CorporateID, type: "combined" | "challenge" }
  const [slides, setSlides]                           = useState([]);
  const [isSlidesLoading, setIsSlidesLoading]         = useState(true);
  const [activeIdx, setActiveIdx]                     = useState(0);
  const [isPaused, setIsPaused]                       = useState(false);

  // Per-slide cache: { [slideID]: { users, isLoading } }
  const [slideData, setSlideData] = useState({});

  const timerRef    = useRef(null);
  const prefetchRef = useRef(null);
  const tokenRef    = useRef(token);
  useEffect(() => { tokenRef.current = token; }, [token]);

  // ── Fetch data for one slide (routes to correct API by type) ────────────────
  const fetchSlide = useCallback(async (slide) => {
    const { _id, type, CorporateID } = slide;

    setSlideData((prev) => {
      if (prev[_id]) return prev; // already cached
      return { ...prev, [_id]: { users: [], isLoading: true } };
    });

    try {
      let users = [];

      if (type === "combined") {
        // AdminGetCorporateLeaderboard — response: data.data.RankedData
        const res  = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetCorporateLeaderboard`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", authorization: `Bearer ${tokenRef.current}` },
            body: JSON.stringify({ CorporateID }),
          },
        );
        const data = await res.json();
        users =
          res.status === 200 && data.status === 200
            ? (data.data?.RankedData ?? []).slice(0, 11).map((u) => ({
                _id:            u._id,
                Name:           u.Name,
                ProfileImage:   u.ProfileImage,
                ChallengeAtmpt: u.ChallengeAtmpt,
                Rank:           u.Rank,
              }))
            : [];
      } else {
        // AdminGetUserListAttemptedChallanges — response: data.data.data
        const res  = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetUserListAttemptedChallanges`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", authorization: `Bearer ${tokenRef.current}` },
            body: JSON.stringify({ ChallengeID: _id }),
          },
        );
        const data = await res.json();
        users =
          res.status === 200 && data.status === 200
            ? (data.data?.data ?? []).slice(0, 11).map((u) => ({
                _id:            u._id,
                Name:           u.Name,
                ProfileImage:   u.ProfileImage,
                ChallengeAtmpt: u.ChallengeAtmpt,
                Rank:           u.Rank,
              }))
            : [];
      }

      setSlideData((prev) => ({ ...prev, [_id]: { users, isLoading: false } }));
    } catch (_) {
      setSlideData((prev) => ({ ...prev, [slide._id]: { users: [], isLoading: false } }));
    }
  }, []);

  // ── Fetch all corporate IDs → challenges → build slide list ─────────────────
  useEffect(() => {
    const fetchAll = async () => {
      setIsSlidesLoading(true);
      try {
        // 1. Get corporate IDs
        const configRes  = await fetch(
          `${ARENO_API_ENDPOINT}/challenges/AdminGetCompetetiveChallengeConfig`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
          },
        );
        const configData = await configRes.json();
        if (configRes.status !== 200 || configData.status !== 200) {
          toast.error(configData.error || "Failed to load config", { position: "bottom-left", autoClose: 3000, hideProgressBar: true });
          return;
        }

        const corporateIDs = configData.data?.CorporateIDs ?? [];
        if (corporateIDs.length === 0) return;

        // 2. Fetch challenges for all corporate IDs in parallel
        const challengeResults = await Promise.all(
          corporateIDs.map((id) =>
            fetch(
              `${ARENO_API_ENDPOINT}/challenges/AdminGetCorporateChallengesForCorporateID`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json", authorization: `Bearer ${token}` },
                body: JSON.stringify({ CorporateID: id }),
              },
            )
              .then((r) => r.json())
              .then((d) =>
                d.status === 200
                  ? (d.data ?? []).map((c) => ({ _id: c._id, Name: c.Name, CorporateID: id, type: "challenge" }))
                  : [],
              )
              .catch(() => []),
          ),
        );

        // 3. Build slide list: for each org → Combined slide first, then its challenges
        const allSlides = corporateIDs.flatMap((id, i) => [
          // Combined slide — synthetic ID so it doesn't clash with challenge IDs
          { _id: `combined_${id}`, Name: `${id} Combined`, CorporateID: id, type: "combined" },
          ...challengeResults[i],
        ]);

        setSlides(allSlides);
      } catch (error) {
        toast.error(error.message, { position: "bottom-left", autoClose: 5000 });
      } finally {
        setIsSlidesLoading(false);
      }
    };

    fetchAll();
  }, [token]);

  // ── Carousel helpers ─────────────────────────────────────────────────────────
  const startCarousel = useCallback((fromIdx, slideList) => {
    clearInterval(timerRef.current);
    clearTimeout(prefetchRef.current);
    if (slideList.length < 2) return;

    const idxRef = { current: fromIdx };

    const schedulePrefetch = () => {
      clearTimeout(prefetchRef.current);
      const nextIdx = (idxRef.current + 1) % slideList.length;
      prefetchRef.current = setTimeout(() => {
        fetchSlide(slideList[nextIdx]);
      }, SLIDE_INTERVAL_MS - PREFETCH_BEFORE_MS);
    };

    schedulePrefetch();

    timerRef.current = setInterval(() => {
      const nextIdx = (idxRef.current + 1) % slideList.length;
      idxRef.current = nextIdx;
      setActiveIdx(nextIdx);
      schedulePrefetch();
    }, SLIDE_INTERVAL_MS);
  }, [fetchSlide]);

  const stopCarousel = useCallback(() => {
    clearInterval(timerRef.current);
    clearTimeout(prefetchRef.current);
  }, []);

  // ── Start carousel once slides are loaded ───────────────────────────────────
  useEffect(() => {
    if (slides.length === 0) return;
    fetchSlide(slides[0]);
    if (!isPaused) startCarousel(0, slides);
    return () => stopCarousel();
  }, [slides]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pause / Resume ───────────────────────────────────────────────────────────
  const handleTogglePause = () => {
    if (isPaused) {
      // Resume from current slide
      startCarousel(activeIdx, slides);
      setIsPaused(false);
    } else {
      stopCarousel();
      setIsPaused(true);
    }
  };

  // ── Manual tab click ────────────────────────────────────────────────────────
  const handleTabClick = (idx) => {
    setActiveIdx(idx);
    fetchSlide(slides[idx]);
    if (!isPaused) startCarousel(idx, slides);
  };

  const activeSlide = slides[activeIdx];
  const activeData  = activeSlide
    ? (slideData[activeSlide._id] ?? { users: [], isLoading: true })
    : { users: [], isLoading: true };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: BG, px: 3, py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 3, "& *": { color: `${SEC} !important` }, "& .MuiBreadcrumbs-separator": { color: `${BORDER} !important` } }}>
        <Link underline="hover" href={BASE_PATH_TO_HOME} sx={{ color: SEC }}>Dashboard</Link>
        <Link underline="hover" sx={{ color: SEC }}>Corporate Challenges</Link>
        <Link underline="hover" href={BASE_PATH_TO_LEADERBOARD} sx={{ color: SEC }}>Leaderboard</Link>
        <Typography fontWeight={500} sx={{ color: PRI }}>Clash Leaderboards</Typography>
      </Breadcrumbs>

      {/* Carousel content */}
      {isSlidesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress sx={{ color: PURPLE }} />
        </Box>
      ) : slides.length === 0 ? (
        <Box sx={{ py: 10, textAlign: "center" }}>
          <FitnessCenterIcon sx={{ fontSize: 48, color: SEC, opacity: 0.4 }} />
          <Typography color={SEC} mt={1}>No challenges found</Typography>
        </Box>
      ) : (
        <>
          <LeaderboardSlide
            key={activeSlide._id}
            slide={activeSlide}
            users={activeData.users}
            isLoading={activeData.isLoading}
          />

          {/* Progress bar + pause button */}
          <Stack direction="row" alignItems="center" spacing={2} sx={{ maxWidth: 640, mx: "auto", mt: 4, mb: 3 }}>
            <Box
              sx={{ flex: 1, height: 2, backgroundColor: BORDER, borderRadius: 1, overflow: "hidden" }}
            >
              <Box
                key={isPaused ? `${activeIdx}-paused` : `${activeIdx}-progress`}
                sx={{
                  height: "100%",
                  backgroundColor: PURPLE,
                  borderRadius: 1,
                  ...(isPaused
                    ? { width: "100%", opacity: 0.3 }
                    : {
                        animation: `slideProgress ${SLIDE_INTERVAL_MS}ms linear`,
                        "@keyframes slideProgress": {
                          from: { width: "0%" },
                          to:   { width: "100%" },
                        },
                      }),
                }}
              />
            </Box>
            <Box
              onClick={handleTogglePause}
              title={isPaused ? "Resume auto-rotation" : "Pause auto-rotation"}
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                border: `1px solid ${isPaused ? PURPLE : BORDER}`,
                backgroundColor: isPaused ? `${PURPLE}22` : "transparent",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                transition: "all 0.2s ease",
                "&:hover": { borderColor: PURPLE, backgroundColor: `${PURPLE}22` },
              }}
            >
              {isPaused
                ? <PlayArrowRoundedIcon sx={{ fontSize: 18, color: PURPLE }} />
                : <PauseRoundedIcon sx={{ fontSize: 18, color: SEC }} />
              }
            </Box>
          </Stack>

          {/* Tabs */}
          <Stack direction="row" flexWrap="wrap" justifyContent="center" gap={2} mt={3}>
            {slides.map((s, i) => (
              <Box
                key={s._id}
                onClick={() => handleTabClick(i)}
                sx={{
                  px: 3.5,
                  py: 1.5,
                  borderRadius: 1,
                  border: `1px solid ${i === activeIdx ? PURPLE : s.type === "combined" ? `${PURPLE}55` : BORDER}`,
                  backgroundColor: i === activeIdx ? `${PURPLE}22` : s.type === "combined" ? `${PURPLE}0d` : CARD_BG,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                  "&:hover": { borderColor: PURPLE, backgroundColor: `${PURPLE}18` },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75} mb={0.25}>
                  <Typography variant="body2" fontWeight={500} sx={{ color: SEC }}>
                    {s.CorporateID}
                  </Typography>
                  {s.type === "combined" && (
                    <Box sx={{ px: 0.75, py: 0.1, borderRadius: 0.5, backgroundColor: `${PURPLE}33` }}>
                      <Typography sx={{ fontSize: 9, fontWeight: 700, color: PURPLE, letterSpacing: 0.8 }}>COMBINED</Typography>
                    </Box>
                  )}
                </Stack>
                <Typography variant="body1" fontWeight={700} sx={{ color: i === activeIdx ? PURPLE : SEC, letterSpacing: 0.5 }}>
                  {s.type === "combined" ? "Combined" : s.Name}
                </Typography>
              </Box>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default AllLeaderboards;
